import {
  Button,
  FlexBox,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { LanguageId } from 'lib-knowledge-delivery-common/graphql';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { ampli } from 'lib-shared-tracking';
import { useNavigate } from 'react-router-dom';
import { useRestoreVersion } from 'versionHistory/hooks/useRestoreVersion';
import { Version } from 'versionHistory/hooks/useVersions';
import { formatDate } from 'versionHistory/utils';

type ArticleTranslationVersionRestoreModalProps = {
  versionMeta: Version;
  articleId: string;
  languageId: LanguageId;
  redirectToArticleEditorAfterRestore: boolean;
};

export function ArticleTranslationVersionRestoreModal({
  versionMeta,
  articleId,
  languageId,
  redirectToArticleEditorAfterRestore,
}: ArticleTranslationVersionRestoreModalProps): JSX.Element {
  const navigate = useNavigate();
  const { hideModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const { restore, loading } = useRestoreVersion({
    articleId,
    languageId,
  });
  const restoreVersion = async () => {
    try {
      await restore(versionMeta.versionNumber, true);
      ampli.knowledgeArticleRestored();
      successNotify({
        title: 'Version restored',
        text: 'The selected version has been restored and published.',
      });
      redirectToArticleEditorAfterRestore &&
        navigate(
          routeGenerator.articleEdit({
            articleId,
            languageId: languageId ?? undefined,
          }),
        );
    } catch {
      errorNotify({
        title: 'Error',
        text: 'Failed to restore version. Please try again.',
      });
    } finally {
      hideModal();
    }
  };

  return (
    <ModalWrapper>
      <ModalHead title="Restore version" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you want to restore to
          {` ${formatDate(versionMeta.publishedAt)}`} version?
        </Text.Paragraph>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button
            isLoading={loading}
            variant="primary"
            onClick={restoreVersion}
          >
            Restore
          </Button>
          <Button isLoading={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
